import React, { useCallback, useState } from "react";
import { Pressable, ScrollView, StyleSheet } from "react-native";
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from "react-native-reanimated";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { View } from "~/components/View";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { BodyText } from "../V2/Text/BodyText";
import { BrandText } from "../V2/Text/BrandText";
import { AlertContext } from "./AlertContext";
import type { ContextType } from "./AlertContext";
import type { IconProps } from "../Icon";
import type { AlertButton } from "react-native";

const defaultOptions = {
  cancelable: false,
  onDismiss: () => {},
  buttonsContainerStyle: {},
};

const S = StyleSheet.create({
  flex: { flex: 1 },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: styleConstantsV2.colorBackgroundBackdrop,
  },

  popup: {
    maxHeight: "80%",
    width: "100%",
    maxWidth: 615,
    borderRadius: styleConstantsV2.borderRadius24,
    backgroundColor: styleConstantsV2.colorWhite,
    padding: styleConstantsV2.spacing16,
    paddingVertical: styleConstantsV2.spacing48,
  },
  popupContainer: {
    paddingTop: styleConstantsV2.spacing72,
    paddingHorizontal: styleConstantsV2.spacing16,
  },
});

interface AlertProviderProps {
  children: React.ReactNode;
}

export function AlertProvider({ children, ...otherProps }: AlertProviderProps) {
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertParams, setAlertParams] = useState({
    title: "",
    body: "",
    buttons: [] as AlertButton[],
    options: defaultOptions,
    iconName: "" as IconProps["name"] | undefined,
  });

  const alert: ContextType["alert"] = useCallback(
    (
      title = "",
      body = "",
      buttons = [],
      iconName,
      options = defaultOptions,
    ) => {
      const preparedOptions = { ...defaultOptions, ...options };
      setAlertParams({
        title,
        body,
        buttons,
        options: preparedOptions,
        iconName,
      });
      setAlertVisible(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const contextValue = { alertVisible, setAlertVisible, alert };

  const { title, body, buttons, options, iconName } = alertParams;

  const { onDismiss, cancelable, buttonsContainerStyle } = options;

  const handleModalPress = () => {
    if (cancelable) {
      onDismiss();
      setAlertVisible(false);
    }
  };

  const buttonsRender = buttons.map((button, index) => {
    return (
      <React.Fragment key={button.text}>
        {index !== 0 && <Spacer medium />}
        <ButtonV2
          secondary={button.style === "default"}
          destructive={button.style === "destructive"}
          title={button.text}
          onPress={() => {
            setAlertVisible(false);
            return button.onPress?.();
          }}
        />
      </React.Fragment>
    );
  });

  return (
    <AlertContext.Provider value={contextValue} {...otherProps}>
      {children}
      {alertVisible && (
        <Modal>
          <Animated.View style={S.backdrop} entering={FadeIn} exiting={FadeOut}>
            <Pressable style={S.flex} onPress={handleModalPress} />
          </Animated.View>
          <View
            flex
            alignItemsCenter
            pointerEvents="box-none"
            style={S.popupContainer}
          >
            {/* Popup / Modal container */}
            <Animated.View
              entering={FadeInDown}
              exiting={FadeOutDown}
              style={S.popup}
            >
              {iconName && (
                <View centerContent>
                  <Icon name={iconName} />
                </View>
              )}
              <ScrollView bounces={false} showsVerticalScrollIndicator={false}>
                {!!title && (
                  <View paddingVerticalMedium>
                    <BrandText weightExtraBold sizeH2 alignCenter colorBlack>
                      {title}
                    </BrandText>
                  </View>
                )}

                {!!body && (
                  <View paddingVerticalMedium>
                    <BodyText alignCenter sizeLarge weightRegular>
                      {body}
                    </BodyText>
                  </View>
                )}
              </ScrollView>

              <Spacer />

              <View
                flexDirectionRow
                justifyContentCenter
                style={buttonsContainerStyle}
              >
                {buttonsRender.length === 0 ? (
                  <ButtonV2
                    secondary
                    title={intl.formatMessage({
                      id: "C3dd3n",
                      defaultMessage: "OK",
                      description: "Alert button",
                    })}
                    onPress={() => {
                      setAlertVisible(false);
                    }}
                  />
                ) : (
                  buttonsRender
                )}
              </View>
            </Animated.View>
          </View>
        </Modal>
      )}
    </AlertContext.Provider>
  );
}
